import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { 
  ArrowLeft, 
  RotateCw, 
  Eye, 
  ChevronDown, 
  ChevronUp, 
  CheckCircle, 
  XCircle, 
  Layout, 
  User, 
  Settings, 
  Calendar,
  AlertCircle,
  Download,
  ExternalLink,
  FileText,
  X,
  Info,
  CheckCircle2,
  Gauge,
  HardDrive,
  Copy,
  Bot,
  Gavel,
  Target
} from 'lucide-react';
import { faChartBar } from '@fortawesome/free-solid-svg-icons';

import {
  PageContainer,
  Card,
  Button,
  SearchInput,
  Table,
  TableHeader,
  TableRow,
  TableCell,
  StatusBadge,
  fadeIn
} from '../styles/PageStyles';
import PageHeaderWithInfo from '../components/common/PageHeaderWithInfo';
import Pagination from '../components/common/Pagination';
import {
  getEvalReports,
  getEvalPacks,
  getEvalTargets,
  getEvalReportsCount,
  getEvalReportsPaginated
} from '../services/aiEvalService';

// --- Styled Components ---

const FilterBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  gap: 1rem;
  flex-wrap: wrap;
`;

const TabGroup = styled.div`
  display: flex;
  background: #f1f5f9;
  padding: 4px;
  border-radius: 8px;
  gap: 4px;
`;

const TabButton = styled.button`
  background: ${props => props.active ? '#ffffff' : 'transparent'};
  color: ${props => props.active ? 'var(--primary-600)' : '#64748b'};
  border: none;
  padding: 6px 12px;
  font-size: 0.85rem;
  font-weight: 500;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: ${props => props.active ? '0 1px 3px rgba(0, 0, 0, 0.05)' : 'none'};

  &:hover {
    color: var(--primary-600);
  }
`;

// Run Detail Layout
const MetricGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const MetricCard = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);

  .label {
    font-size: 0.75rem;
    font-weight: 600;
    color: #64748b;
    text-transform: uppercase;
    margin-bottom: 4px;
    letter-spacing: 0.5px;
  }

  .value {
    font-size: 1.35rem;
    font-weight: 700;
    color: #0f172a;
  }

  .sub {
    font-size: 0.75rem;
    color: #94a3b8;
    margin-top: 2px;
  }
`;

const CaseResultRow = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  margin-bottom: 12px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.01);
  transition: all 0.2s ease;

  &:hover {
    border-color: #cbd5e1;
  }
`;

const CaseHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px 16px;
  cursor: pointer;
  background: #ffffff;
  user-select: none;
`;

const CaseBody = styled.div`
  padding: 16px;
  border-top: 1px solid #f1f5f9;
  background: #f8fafc;
`;

const KVBlock = styled.div`
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }

  .kv-label {
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    color: #64748b;
    margin-bottom: 4px;
    letter-spacing: 0.5px;
  }

  .kv-val {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 8px 12px;
    font-family: monospace;
    font-size: 0.825rem;
    color: #0f172a;
    white-space: pre-wrap;
    line-height: 1.5;
  }
`;

const BarChartContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
`;

const BarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  
  .bar-label {
    width: 60px;
    font-size: 0.75rem;
    color: #64748b;
    font-family: monospace;
  }

  .bar-track {
    flex: 1;
    height: 10px;
    background: #e2e8f0;
    border-radius: 5px;
    overflow: hidden;
  }

  .bar-fill {
    height: 100%;
    background: var(--primary-500);
    border-radius: 5px;
    transition: width 0.5s ease;
  }

  .bar-value {
    width: 48px;
    font-size: 0.75rem;
    color: #0f172a;
    font-family: monospace;
    text-align: right;
  }
`;

// Toast Notification
const Toast = styled.div`
  position: fixed;
  bottom: 24px;
  right: 24px;
  background: #1e293b;
  color: #ffffff;
  border-radius: 8px;
  padding: 12px 18px;
  display: flex;
  align-items: center;
  gap: 10px;
  box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1);
  z-index: 3000;
  font-size: 0.85rem;
  animation: popIn 0.2s cubic-bezier(0.16, 1, 0.3, 1);

  @keyframes popIn {
    from { transform: scale(0.9) translateY(12px); opacity: 0; }
    to { transform: scale(1) translateY(0); opacity: 1; }
  }
`;

// --- Accuracy Report High-Fidelity Styled Components ---
const DetailLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  color: #0f172a;
`;

const DetailHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  flex-wrap: wrap;
  gap: 12px;
`;

const HeaderNav = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  color: #64748b;
  font-size: 0.85rem;
  font-weight: 500;
  
  &:hover {
    color: var(--primary-600);
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const ReportTitleBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 8px;

  .icon-wrapper {
    width: 44px;
    height: 44px;
    background: #fef2f2;
    color: #ef4444;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  h1 {
    font-size: 1.35rem;
    font-weight: 700;
    color: #0f172a;
    margin: 0;
  }
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: 1.6fr 1fr;
  gap: 20px;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const InfoCard = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.02);
`;

const InfoRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 16px;
  
  @media (max-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const InfoBox = styled.div`
  background: #f8fafc;
  border: 1px solid #f1f5f9;
  border-radius: 8px;
  padding: 8px 12px;

  label {
    display: block;
    font-size: 0.68rem;
    font-weight: 600;
    color: #64748b;
    margin-bottom: 2px;
    text-transform: uppercase;
  }

  span {
    font-size: 0.825rem;
    font-weight: 600;
    color: #0f172a;
    font-family: ${props => props.mono ? 'monospace' : 'inherit'};
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const SubGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 16px;
  border-bottom: 1px solid #f1f5f9;
  padding-bottom: 16px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const DateMetaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  font-size: 0.78rem;
  color: #64748b;
  border-top: 1px solid #f1f5f9;
  padding-top: 16px;

  div {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  span.label {
    font-weight: 600;
    color: #94a3b8;
    display: flex;
    align-items: center;
    gap: 4px;
  }

  span.val {
    color: #334155;
    font-weight: 500;
  }
`;

const SummaryPanel = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.02);
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const DialSection = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 8px;
  border-bottom: 1px solid #f1f5f9;
  padding-bottom: 16px;
`;

const SummaryStatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
`;

const SummaryStatCard = styled.div`
  background: #ffffff;
  border: 1px solid #f1f5f9;
  border-radius: 8px;
  padding: 10px 12px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.01);

  .label {
    font-size: 0.72rem;
    font-weight: 600;
    color: #64748b;
    margin-bottom: 4px;
  }

  .value {
    font-size: 1.25rem;
    font-weight: 700;
  }

  &.total .value { color: #2563eb; }
  &.passed .value { color: #16a34a; }
  &.failed .value { color: #dc2626; }
  &.rate .value { color: #ea580c; }
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 12px;
  margin-bottom: 16px;
`;

const FilterControls = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const SelectInput = styled.select`
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid #cbd5e1;
  background: #ffffff;
  font-size: 0.85rem;
  color: #334155;
  outline: none;
  cursor: pointer;

  &:focus {
    border-color: var(--primary-500);
  }
`;

const TestCasesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const PremiumCaseCard = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-left: 4px solid ${props => props.passed ? '#22c55e' : '#ef4444'};
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,0.01);
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 2px 5px rgba(0,0,0,0.03);
    border-color: #cbd5e1;
  }
`;

const CaseHeaderRow = styled.div`
  display: flex;
  align-items: center;
  padding: 14px 16px;
  cursor: pointer;
  user-select: none;
  gap: 12px;

  .name-block {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 700;
    font-size: 0.95rem;
    color: #1e293b;
  }

  .meta-details {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px 10px;
    font-size: 0.78rem;
    color: #64748b;
    margin-left: 12px;
    flex: 1;

    span.sep {
      color: #cbd5e1;
    }
  }
`;

const CaseContent = styled.div`
  padding: 16px;
  border-top: 1px solid #f1f5f9;
  background: #f8fafc;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ReasonText = styled.div`
  font-size: 0.8rem;
  color: #334155;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px 14px;
  line-height: 1.5;

  span.label {
    font-weight: 700;
    color: #475569;
    margin-right: 6px;
  }

  button.toggle-link {
    background: none;
    border: none;
    color: var(--primary-600);
    font-size: 0.78rem;
    font-weight: 600;
    cursor: pointer;
    padding: 0;
    margin-left: 6px;
    display: inline-flex;
    align-items: center;
    gap: 2px;
  }
`;

const ExecutionRunsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 8px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  display: table;

  th, td {
    padding: 10px 12px;
    text-align: left;
    font-size: 0.78rem;
    border-bottom: 1px solid #f1f5f9;
  }

  th {
    background: #f8fafc;
    font-weight: 600;
    color: #475569;
    text-transform: uppercase;
    font-size: 0.7rem;
    letter-spacing: 0.5px;
  }

  tr:last-child td {
    border-bottom: none;
  }
`;

const ScoreBadge = styled.span`
  background: ${props => props.color === 'green' ? '#dcfce7' : '#fee2e2'};
  color: ${props => props.color === 'green' ? '#15803d' : '#b91c1c'};
  padding: 3px 8px;
  border-radius: 6px;
  font-weight: 700;
  font-size: 0.72rem;
  font-family: monospace;
  display: inline-block;
`;

const RunDetailModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 23, 42, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 5000;
  backdrop-filter: blur(4px);
`;

const RunDetailModalContainer = styled.div`
  background: #ffffff;
  border-radius: 12px;
  width: 90%;
  max-width: 700px;
  max-height: 85vh;
  overflow-y: auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  animation: modalPop 0.25s cubic-bezier(0.16, 1, 0.3, 1);

  @keyframes modalPop {
    from { transform: scale(0.95); opacity: 0; }
    to { transform: scale(1); opacity: 1; }
  }
`;

const RunDetailModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #f1f5f9;

  h3 {
    margin: 0;
    font-size: 1.05rem;
    font-weight: 700;
    color: #0f172a;
  }
`;

const RunDetailModalBody = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

// SVG Half-Circle Radial Dial component
const RadialGauge = ({ score, threshold }) => {
  const scoreVal = score !== undefined ? score : 0;
  const radius = 55;
  const strokeWidth = 12;
  const circumference = Math.PI * radius; // 172.78
  const offset = circumference - (Math.min(Math.max(scoreVal, 0), 1) * circumference);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', width: '100%' }}>
      <svg width="180" height="110" style={{ overflow: 'visible' }}>
        <path
          d="M 20,95 A 55,55 0 0,1 160,95"
          fill="none"
          stroke="#f1f5f9"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
        <path
          d="M 20,95 A 55,55 0 0,1 160,95"
          fill="none"
          stroke={scoreVal >= threshold ? '#22c55e' : '#f59e0b'}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 0.8s ease' }}
        />
      </svg>
      <div style={{ position: 'absolute', bottom: '10px', textAlign: 'center' }}>
        <span style={{ fontSize: '1.85rem', fontWeight: 800, color: '#0f172a', display: 'block', lineHeight: 1.1 }}>
          {scoreVal.toFixed(2)}
        </span>
        <span style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600 }}>
          Average Test Score
        </span>
      </div>
    </div>
  );
};

const IterationLineChart = ({ title, unit, data, color, isPassed }) => {
  if (!data || data.length === 0) {
    return (
      <div style={{ height: '110px', background: '#fafafa', borderRadius: '8px', border: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', color: '#94a3b8' }}>
        No iteration data
      </div>
    );
  }

  const width = 220;
  const height = 110;
  const padding = { top: 16, right: 15, bottom: 22, left: 35 };
  const graphWidth = width - padding.left - padding.right;
  const graphHeight = height - padding.top - padding.bottom;

  const minVal = Math.min(...data) * 0.95;
  const maxVal = Math.max(...data) * 1.05 || 1;

  const points = data.map((val, idx) => {
    const x = padding.left + (data.length > 1 ? (idx / (data.length - 1)) * graphWidth : graphWidth / 2);
    const y = padding.top + graphHeight - ((val - minVal) / (maxVal - minVal || 1)) * graphHeight;
    return { x, y, val };
  });

  const pathD = points.reduce((acc, pt, i) => `${acc} ${i === 0 ? 'M' : 'L'} ${pt.x} ${pt.y}`, '');
  const areaD = `${pathD} L ${points[points.length - 1].x} ${height - padding.bottom} L ${points[0].x} ${height - padding.bottom} Z`;

  const strokeColor = color || (isPassed ? '#22c55e' : '#ef4444');
  const fillColor = color ? `${color}20` : (isPassed ? '#22c55e20' : '#ef444420');

  const formatY = (v) => {
    if (v >= 1000) return `${Math.round(v / 1000)}K`;
    if (v < 10) return v.toFixed(1);
    return Math.round(v);
  };

  return (
    <div style={{ background: '#fafafa', padding: '8px 10px', borderRadius: '8px', border: '1px solid #f1f5f9' }}>
      <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569', marginBottom: '4px' }}>
        {title} ({unit})
      </div>
      <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`}>
        <line x1={padding.left} y1={padding.top} x2={width - padding.right} y2={padding.top} stroke="#e2e8f0" strokeDasharray="3 3" />
        <line x1={padding.left} y1={padding.top + graphHeight / 2} x2={width - padding.right} y2={padding.top + graphHeight / 2} stroke="#e2e8f0" strokeDasharray="3 3" />
        <line x1={padding.left} y1={height - padding.bottom} x2={width - padding.right} y2={height - padding.bottom} stroke="#cbd5e1" />

        <text x={padding.left - 4} y={padding.top + 3} textAnchor="end" fontSize="8.5" fill="#94a3b8">{formatY(maxVal)}</text>
        <text x={padding.left - 4} y={padding.top + graphHeight / 2 + 3} textAnchor="end" fontSize="8.5" fill="#94a3b8">{formatY((maxVal + minVal) / 2)}</text>
        <text x={padding.left - 4} y={height - padding.bottom + 1} textAnchor="end" fontSize="8.5" fill="#94a3b8">{formatY(minVal)}</text>

        <path d={areaD} fill={fillColor} />
        <path d={pathD} fill="none" stroke={strokeColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />

        {points.map((pt, idx) => (
          <g key={idx}>
            <circle cx={pt.x} cy={pt.y} r="3" fill={strokeColor} stroke="#ffffff" strokeWidth="1.5" />
            <text x={pt.x} y={height - 6} textAnchor="middle" fontSize="8.5" fill="#64748b">{idx + 1}</text>
          </g>
        ))}
      </svg>
    </div>
  );
};

// --- MAIN COMPONENT ---

const EvalReports = () => {
  const [reports, setReports] = useState([]);
  const [packs, setPacks] = useState([]);
  const [targets, setTargets] = useState({ agent: [], planner: [], pipeline: [] });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [viewAll, setViewAll] = useState(false);
  const pageSize = 5;
  
  // Navigation views: 'list' or 'detail'
  const [view, setView] = useState('list');
  const [selectedReport, setSelectedReport] = useState(null);
  
  // Filtering & search
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilterTab, setActiveFilterTab] = useState('all');
  
  // Collapsible test cases state
  const [expandedCases, setExpandedCases] = useState({});

  const [toastMsg, setToastMsg] = useState(null);

  // High-fidelity detail view state hooks
  const [statusFilter, setStatusFilter] = useState('all');
  const [showTruncatedReason, setShowTruncatedReason] = useState({});
  const [activeModalRun, setActiveModalRun] = useState(null);

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      const filter = {};
      if (activeFilterTab !== 'all') {
        filter.status = activeFilterTab;
      }
      if (searchTerm) {
        filter.run_id = { ilike: searchTerm };
      }

      const count = await getEvalReportsCount(filter);
      setTotalCount(count);

      const skip = viewAll ? 0 : (currentPage - 1) * pageSize;
      const limit = viewAll ? 100000 : pageSize;

      const [reportsData, packsData, targetsData] = await Promise.all([
        getEvalReportsPaginated(filter, skip, limit),
        getEvalPacks(),
        getEvalTargets()
      ]);
      setReports(reportsData || []);
      setPacks(packsData || []);
      setTargets(targetsData || { agent: [], planner: [], pipeline: [] });
    } catch (err) {
      console.error(err);
    }
  }, [activeFilterTab, searchTerm, currentPage, pageSize, viewAll]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Listen to async execution triggers for live refresh
  useEffect(() => {
    const handleFinished = () => {
      fetchData();
      setToastMsg('Evaluation queued job has finished executing.');
      setTimeout(() => setToastMsg(null), 3500);
    };

    window.addEventListener('bmm-eval-report-finished', handleFinished);
    return () => window.removeEventListener('bmm-eval-report-finished', handleFinished);
  }, [fetchData]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, activeFilterTab]);

  const handleRefresh = () => {
    fetchData();
    setToastMsg('Refreshed evaluation reports list.');
    setTimeout(() => setToastMsg(null), 2500);
  };

  const getPackDetails = (packId) => {
    return packs.find(p => p.id === packId) || { name: 'Unknown Evaluation Pack', code: 'EVAL-UNKNOWN', eval_type: 'accuracy' };
  };

  const getTargetName = (targetType, targetCode) => {
    if (!targetCode) return '—';
    const list = targets[targetType?.toLowerCase()] || [];
    const match = list.find(t => t.code === targetCode);
    if (match && match.name) {
      return match.name;
    }
    return targetCode;
  };

  const getStatusBadgeType = (status) => {
    switch (status?.toLowerCase()) {
      case 'passed': return 'active';
      case 'failed': return 'inactive';
      case 'running': return 'warning';
      default: return 'default';
    }
  };

  const getPackIcon = (pack) => {
    const name = pack.name?.toLowerCase() || '';
    if (name === 'performance pipeline') {
      return { icon: <Gauge size={16} />, bg: '#f5f3ff', color: '#8b5cf6' };
    }
    if (name === 'accuracy pipeline') {
      return { icon: <Target size={16} />, bg: '#f0fdf4', color: '#22c55e' };
    }
    if (name === 'fetch_weather_info_performance') {
      return { icon: <Bot size={16} />, bg: '#eff6ff', color: '#3b82f6' };
    }
    if (name === 'fetch_weather_info_judge') {
      return { icon: <Gavel size={16} />, bg: '#fffbeb', color: '#d97706' };
    }
    if (name === 'fetch_weather_info') {
      return { icon: <Target size={16} />, bg: '#eff6ff', color: '#3b82f6' };
    }
    if (name === 'test') {
      return { icon: <FileText size={16} />, bg: '#f8fafc', color: '#64748b' };
    }
    // Generic fallback based on target/eval type
    if (pack.eval_type === 'performance') {
      return { icon: <Gauge size={16} />, bg: '#f5f3ff', color: '#8b5cf6' };
    }
    if (pack.eval_type === 'agent_as_judge') {
      return { icon: <Gavel size={16} />, bg: '#fffbeb', color: '#d97706' };
    }
    if (pack.target_type === 'agent') {
      return { icon: <Bot size={16} />, bg: '#eff6ff', color: '#3b82f6' };
    }
    return { icon: <Target size={16} />, bg: '#f0fdf4', color: '#22c55e' };
  };

  const handleCopyText = (text, e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(text);
    setToastMsg(`Copied ${text} to clipboard!`);
    setTimeout(() => setToastMsg(null), 2500);
  };

  const formatRelativeTime = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      const now = new Date();
      const diffMs = now - d;
      const diffMins = Math.floor(diffMs / 1000 / 60);
      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      const diffHours = Math.floor(diffMins / 60);
      const minsRemainder = diffMins % 60;
      if (diffHours < 24) return `${diffHours}h ${minsRemainder}m ago`;
      const diffDays = Math.floor(diffHours / 24);
      return `${diffDays}d ago`;
    } catch (e) {
      return '';
    }
  };

  const formatMethodology = (evalType) => {
    if (!evalType) return '—';
    const type = evalType.toLowerCase();
    if (type === 'agent_as_judge') return 'Agent-As-Judge';
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  const renderStatusBadge = (statusText) => {
    const s = statusText?.toLowerCase();
    let bg = '#f1f5f9';
    let color = '#475569';
    let border = '#e2e8f0';
    let dotColor = '#94a3b8';
    let label = statusText || 'Unknown';

    if (s === 'passed') {
      bg = '#f0fdf4';
      color = '#15803d';
      border = '#dcfce7';
      dotColor = '#22c55e';
      label = 'Passed';
    } else if (s === 'failed') {
      bg = '#fef2f2';
      color = '#b91c1c';
      border = '#fee2e2';
      dotColor = '#ef4444';
      label = 'Failed';
    } else if (s === 'running') {
      bg = '#fffbeb';
      color = '#b45309';
      border = '#fef3c7';
      dotColor = '#f59e0b';
      label = 'Running';
    }

    return (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        background: bg,
        color: color,
        border: `1px solid ${border}`,
        padding: '4px 10px',
        borderRadius: '8px',
        fontSize: '0.78rem',
        fontWeight: 600,
        gap: '6px'
      }}>
        <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: dotColor }}></span>
        {label}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      const d = new Date(dateStr);
      return d.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });
    } catch (e) {
      return dateStr;
    }
  };


  const toggleCase = (idx) => {
    setExpandedCases(prev => ({
      ...prev,
      [idx]: !prev[idx]
    }));
  };

  // Open details page
  const handleViewReport = (report) => {
    setSelectedReport(report);
    setExpandedCases({});
    setView('detail');
  };

  // Render metrics for chosen report
  const renderDetailMetrics = (report, pack) => {
    const duration = report.completed_at 
      ? `${((new Date(report.completed_at) - new Date(report.started_at)) / 1000).toFixed(1)}s`
      : '—';

    if (pack.eval_type === 'accuracy' || pack.eval_type === 'agent_as_judge') {
      const passedCount = report.result?.case_results?.filter(c => c.score >= (report.result?.pass_threshold || report.result?.pass_score || 8))?.length || 0;
      return (
        <MetricGrid>
          <MetricCard>
            <div className="label">Avg Score</div>
            <div className="value">{report.result?.avg_score || '—'} <span style={{ fontSize: '0.8rem', color: '#64748b' }}>/ 10</span></div>
            <div className="sub">Threshold: {report.result?.pass_threshold !== undefined && report.result?.pass_threshold !== null ? report.result.pass_threshold : (report.result?.pass_score || 8)}</div>
          </MetricCard>
          <MetricCard>
            <div className="label">Cases Passed</div>
            <div className="value">{passedCount} <span style={{ fontSize: '0.8rem', color: '#64748b' }}>/ {report.result?.case_results?.length || 0}</span></div>
            <div className="sub">Accuracy target</div>
          </MetricCard>
          <MetricCard>
            <div className="label">Environment</div>
            <div className="value" style={{ textTransform: 'capitalize' }}>{report.environment}</div>
            <div className="sub">Execution scope</div>
          </MetricCard>
          <MetricCard>
            <div className="label">Duration</div>
            <div className="value">{duration}</div>
            <div className="sub">Sync run profile</div>
          </MetricCard>
        </MetricGrid>
      );
    }

    if (pack.eval_type === 'reliability') {
      const passedCount = report.result?.case_results?.filter(c => c.passed)?.length || 0;
      return (
        <MetricGrid>
          <MetricCard>
            <div className="label">Cases Passed</div>
            <div className="value">{passedCount} <span style={{ fontSize: '0.8rem', color: '#64748b' }}>/ {report.result?.case_results?.length || 0}</span></div>
            <div className="sub">No tool call drops</div>
          </MetricCard>
          <MetricCard>
            <div className="label">Order Enforced</div>
            <div className="value">{pack.settings?.expected_call_order ? 'Yes' : 'No'}</div>
            <div className="sub">Call sequences check</div>
          </MetricCard>
          <MetricCard>
            <div className="label">Environment</div>
            <div className="value" style={{ textTransform: 'capitalize' }}>{report.environment}</div>
            <div className="sub">Execution scope</div>
          </MetricCard>
          <MetricCard>
            <div className="label">Duration</div>
            <div className="value">{duration}</div>
            <div className="sub">Orchestrator profile</div>
          </MetricCard>
        </MetricGrid>
      );
    }

    if (pack.eval_type === 'performance') {
      return (
        <MetricGrid>
          <MetricCard>
            <div className="label">Avg Latency</div>
            <div className="value">{report.result?.avg_latency_s || '—'}s</div>
            <div className="sub">Test average</div>
          </MetricCard>
          <MetricCard>
            <div className="label">P95 Latency</div>
            <div className="value">{report.result?.p95_latency_s || '—'}s</div>
            <div className="sub">SLA budget: {pack.settings?.max_latency_ms ? `${pack.settings.max_latency_ms / 1000}s` : 'None'}</div>
          </MetricCard>
          <MetricCard>
            <div className="label">Avg Peak Memory</div>
            <div className="value">{report.result?.avg_memory_mib || '—'} <span style={{ fontSize: '0.8rem', color: '#64748b' }}>MiB</span></div>
            <div className="sub">System budget: {pack.settings?.max_memory_mb ? `${pack.settings.max_memory_mb}MB` : 'None'}</div>
          </MetricCard>
          <MetricCard>
            <div className="label">Duration</div>
            <div className="value">{duration}</div>
            <div className="sub">Total iteration tests</div>
          </MetricCard>
        </MetricGrid>
      );
    }

    return null;
  };

  const paginatedReports = reports;

  return (
    <PageContainer>
      <motion.div initial="hidden" animate="visible" variants={fadeIn}>
        {view === 'list' ? (
          <>
            <PageHeaderWithInfo
              icon={faChartBar}
              title="Reports"
              description="Historical metrics log of completed model evaluations. Monitor score profiles, tool execution reliability logs, and peak latency trends."
            >
              <div style={{ display: 'flex', gap: '1rem' }}>
                <SearchInput
                  type="text"
                  placeholder="Search reports..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <Button onClick={handleRefresh}>
                  <RotateCw size={16} /> Refresh
                </Button>
              </div>
            </PageHeaderWithInfo>

            <FilterBar>
              <TabGroup>
                <TabButton active={activeFilterTab === 'all'} onClick={() => setActiveFilterTab('all')}>All Runs</TabButton>
                <TabButton active={activeFilterTab === 'running'} onClick={() => setActiveFilterTab('running')}>Running</TabButton>
                <TabButton active={activeFilterTab === 'passed'} onClick={() => setActiveFilterTab('passed')}>Passed</TabButton>
                <TabButton active={activeFilterTab === 'failed'} onClick={() => setActiveFilterTab('failed')}>Failed</TabButton>
              </TabGroup>
            </FilterBar>

            <Card style={{ padding: 0, overflow: 'hidden' }}>
              <Table>
                <thead>
                  <tr>
                    <TableHeader>Evaluation Pack</TableHeader>
                    <TableHeader>Run ID</TableHeader>
                    <TableHeader>Run At</TableHeader>
                    <TableHeader>Target</TableHeader>
                    <TableHeader>Methodology</TableHeader>
                    <TableHeader>Status</TableHeader>
                    <TableHeader style={{ textAlign: 'right' }}>Actions</TableHeader>
                  </tr>
                </thead>
                <tbody>
                  {paginatedReports.map(report => {
                    const pack = getPackDetails(report.eval_pack_id);
                    const iconData = getPackIcon(pack);
                    return (
                      <TableRow key={report.run_id} onClick={() => handleViewReport(report)} style={{ cursor: 'pointer' }}>
                        <TableCell>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifycontent: 'center',
                              width: '36px',
                              height: '36px',
                              borderRadius: '8px',
                              marginRight: '12px',
                              background: iconData.bg,
                              color: iconData.color,
                              flexShrink: 0,
                              fontSize: '1rem',
                              fontWeight: 'bold'
                            }}>
                              {iconData.icon}
                            </div>
                            <div style={{ fontWeight: 600, color: '#0f172a' }}>{pack.name}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ fontSize: '0.8rem', color: '#475569', fontFamily: 'monospace' }}>{report.run_id}</span>
                            <Copy 
                              size={12} 
                              color="#94a3b8" 
                              style={{ cursor: 'pointer' }} 
                              onClick={(e) => handleCopyText(report.run_id, e)} 
                              title="Copy Run ID"
                            />
                          </div>
                        </TableCell>
                        <TableCell>
                          <div style={{ fontSize: '0.8rem', color: '#0f172a', fontWeight: 500 }}>
                            {formatDate(report.created_at || report.started_at)}
                          </div>
                          {report.started_at && (
                            <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '2px' }}>
                              {formatRelativeTime(report.created_at || report.started_at)}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            background: '#e0f2fe',
                            color: '#0369a1',
                            border: '1px solid #bae6fd',
                            padding: '3px 10px',
                            borderRadius: '14px',
                            fontSize: '0.78rem',
                            fontWeight: 600
                          }}>
                            {getTargetName(pack.target_type, pack.target_code)}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span style={{ fontSize: '0.85rem', fontWeight: 500, color: '#475569' }}>
                            {formatMethodology(pack.eval_type)}
                          </span>
                        </TableCell>
                        <TableCell>
                          {renderStatusBadge(report.status)}
                        </TableCell>
                        <TableCell style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                          <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end', alignItems: 'center' }}>
                            <Button style={{ padding: '4px 8px', fontSize: '0.8rem', display: 'inline-flex', alignItems: 'center', gap: '4px' }} onClick={() => handleViewReport(report)}>
                              <Eye size={12} /> View
                            </Button>
                            <Button style={{ padding: '4px', background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '1.2rem', lineHeight: 1 }} onClick={() => handleViewReport(report)}>
                              ⋮
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {reports.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>
                        No evaluation reports found matching filter.
                      </TableCell>
                    </TableRow>
                  )}
                </tbody>
              </Table>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '16px', paddingBottom: '16px', borderBottom: '1px solid #f1f5f9' }}>
                <Button style={{ padding: '6px 12px', fontSize: '0.8rem' }} onClick={() => setViewAll(!viewAll)}>
                  {viewAll ? 'Show Paginated Reports' : 'Show All Reports'}
                </Button>
              </div>
              <Pagination
                currentPage={currentPage}
                totalPages={Math.ceil(totalCount / pageSize)}
                totalCount={totalCount}
                setCurrentPage={setCurrentPage}
                pageSize={pageSize}
              />
            </Card>
          </>
        ) : (
          /* --- DETAIL DESIGN --- */
          (() => {
            const pack = getPackDetails(selectedReport.eval_pack_id);
            const duration = selectedReport.completed_at 
              ? `${((new Date(selectedReport.completed_at) - new Date(selectedReport.started_at)) / 1000).toFixed(1)}s`
              : '—';

            const downloadReportJSON = (report) => {
              const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(report, null, 2));
              const downloadAnchor = document.createElement('a');
              downloadAnchor.setAttribute("href", dataStr);
              downloadAnchor.setAttribute("download", `${report.code || report.run_id}_report.json`);
              document.body.appendChild(downloadAnchor);
              downloadAnchor.click();
              downloadAnchor.remove();
            };

            // Get runs list for accuracy & judge reports
            const getRunsList = (tc) => {
              if (tc.evaluation_result?.runs && Array.isArray(tc.evaluation_result.runs)) {
                return tc.evaluation_result.runs.map((r, runIdx) => {
                  const res = r.eval_result?.results?.[0] || {};
                  return {
                    runNum: runIdx + 1,
                    runId: r.eval_result?.run_id || `run_${runIdx + 1}`,
                    score: res.score !== undefined ? res.score : r.eval_result?.avg_score,
                    output: res.output || "",
                    reason: res.reason || "",
                    expected_output: res.expected_output || "",
                    criteria: res.criteria || "",
                    passed: res.passed !== undefined ? res.passed : (r.eval_result?.pass_rate >= 100),
                    passRate: r.eval_result?.pass_rate !== undefined ? r.eval_result.pass_rate : 100
                  };
                });
              }
              const singleResults = tc.evaluation_result?.eval_result?.results;
              if (singleResults && Array.isArray(singleResults)) {
                return singleResults.map((res, runIdx) => ({
                  runNum: runIdx + 1,
                  runId: tc.evaluation_result?.eval_result?.run_id || `run_${runIdx + 1}`,
                  score: res.score !== undefined ? res.score : tc.evaluation_result?.eval_result?.avg_score,
                  output: res.output || "",
                  reason: res.reason || "",
                  expected_output: res.expected_output || "",
                  criteria: res.criteria || "",
                  passed: res.passed !== undefined ? res.passed : (tc.evaluation_result?.eval_result?.pass_rate >= 100),
                  passRate: tc.evaluation_result?.eval_result?.pass_rate !== undefined ? tc.evaluation_result.eval_result.pass_rate : 100
                }));
              }
              return [];
            };

            // Filter test cases based on selection
            const reportCases = selectedReport.result?.case_results || [];
            const filteredCases = reportCases.filter(c => {
              if (statusFilter === 'all') return true;
              if (statusFilter === 'passed') return c.passed;
              if (statusFilter === 'failed') return !c.passed;
              return true;
            });

            // Expand all toggle helpers
            const isAllExpanded = filteredCases.length > 0 && filteredCases.every((_, idx) => !!expandedCases[idx]);
            const toggleExpandAll = () => {
              if (isAllExpanded) {
                setExpandedCases({});
              } else {
                const next = {};
                filteredCases.forEach((_, idx) => {
                  next[idx] = true;
                });
                setExpandedCases(next);
              }
            };

            const renderReason = (tc, index) => {
              const reason = tc.reason || "";
              const isLong = reason.length > 150;
              const isExpanded = !!showTruncatedReason[index];

              if (!reason) return null;

              return (
                <ReasonText>
                  <span className="label">Reason:</span>
                  {isLong && !isExpanded ? `${reason.slice(0, 150)}...` : reason}
                  {isLong && (
                    <button 
                      className="toggle-link" 
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowTruncatedReason(prev => ({ ...prev, [index]: !prev[index] }));
                      }}
                    >
                      {isExpanded ? 'Show less' : 'Show more'}
                    </button>
                  )}
                </ReasonText>
              );
            };

            return (
              <DetailLayout>
                {/* 1. Header Bar */}
                <DetailHeader>
                  <HeaderNav onClick={() => setView('list')}>
                    <ArrowLeft size={16} /> Reports / Report Details
                  </HeaderNav>
                </DetailHeader>

                {/* Report Title */}
                <ReportTitleBlock>
                  <div className="icon-wrapper">
                    {selectedReport.eval_type === 'performance' ? <Gauge size={22} color="#2563eb" /> : <FileText size={22} />}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <h1 style={{ margin: 0 }}>{selectedReport.name}</h1>
                    <StatusBadge status={getStatusBadgeType(selectedReport.status)}>
                      {selectedReport.status}
                    </StatusBadge>
                  </div>
                </ReportTitleBlock>

                {/* 2. Main KPI Grid */}
                <DetailGrid>
                  {/* Left Box: General Info */}
                  <InfoCard>
                    <SubGrid>
                      <InfoBox>
                        <label>Run ID</label>
                        <span mono={true}>{selectedReport.run_id}</span>
                      </InfoBox>
                      <InfoBox>
                        <label>Status</label>
                        <div style={{ marginTop: '2px' }}>
                          <StatusBadge status={getStatusBadgeType(selectedReport.status)}>
                            {selectedReport.status}
                          </StatusBadge>
                        </div>
                      </InfoBox>
                    </SubGrid>

                    <InfoRow>
                      <InfoBox>
                        <label>Version</label>
                        <span>{selectedReport.version}</span>
                      </InfoBox>
                      <InfoBox>
                        <label>Target</label>
                        <span>{getTargetName(pack.target_type, pack.target_code)}</span>
                      </InfoBox>
                    </InfoRow>

                    <DateMetaGrid>
                      <div>
                        <span className="label"><Calendar size={12} /> Started At</span>
                        <span className="val">{formatDate(selectedReport.started_at)}</span>
                      </div>
                      <div>
                        <span className="label"><Calendar size={12} /> Completed At</span>
                        <span className="val">{formatDate(selectedReport.completed_at)}</span>
                      </div>
                      <div>
                        <span className="label"><RotateCw size={12} /> Duration</span>
                        <span className="val">{duration}</span>
                      </div>
                      <div>
                        <span className="label"><RotateCw size={12} /> Total Iterations</span>
                        <span className="val">{reportCases.reduce((sum, c) => sum + (c.iterations || 1), 0)}</span>
                      </div>
                    </DateMetaGrid>
                  </InfoCard>

                  {/* Right Box: Performance Summary vs Score Summary */}
                  {selectedReport.eval_type === 'performance' ? (
                    (() => {
                      const perfCases = reportCases.filter(c => c.eval_type === 'performance' || selectedReport.eval_type === 'performance');
                      const perfAvgLatency = perfCases.length > 0 ? Math.round(perfCases.reduce((s, c) => s + (c.avg_latency_ms || 0), 0) / perfCases.length) : 0;
                      const perfMaxLatency = perfCases.length > 0 ? Math.round(Math.max(...perfCases.map(c => c.max_latency_ms || 0))) : 0;
                      const perfMinLatency = perfCases.length > 0 ? Math.round(Math.min(...perfCases.map(c => c.min_latency_ms || c.avg_latency_ms || 0))) : 0;
                      const perfHasFailedLatency = perfCases.some(c => c.passed_latency === false);

                      const perfAvgMemory = perfCases.length > 0 ? (perfCases.reduce((s, c) => s + (c.avg_memory_mb || 0), 0) / perfCases.length) : 0;
                      const perfMaxMemory = perfCases.length > 0 ? Math.max(...perfCases.map(c => c.max_memory_mb || 0)) : 0;
                      const perfP95Memory = perfCases.length > 0 ? Math.max(...perfCases.map(c => c.evaluation_result?.p95_memory_usage || c.max_memory_mb || 0)) : 0;

                      return (
                        <SummaryPanel>
                          <h3 style={{ fontSize: '0.85rem', fontWeight: 700, color: '#334155', margin: '0 0 12px 0' }}>Performance Summary</h3>
                          
                          <SummaryStatsGrid style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: '12px' }}>
                            <SummaryStatCard className="total">
                              <div className="label">Total Cases</div>
                              <div className="value" style={{ color: '#0f172a' }}>{selectedReport.total_cases}</div>
                            </SummaryStatCard>
                            <SummaryStatCard className="passed">
                              <div className="label">Passed Cases</div>
                              <div className="value" style={{ color: '#16a34a' }}>{selectedReport.passed_cases}</div>
                            </SummaryStatCard>
                            <SummaryStatCard className="failed">
                              <div className="label">Failed Cases</div>
                              <div className="value" style={{ color: '#dc2626' }}>{selectedReport.failed_cases}</div>
                            </SummaryStatCard>
                            <SummaryStatCard className="rate">
                              <div className="label">Pass Rate</div>
                              <div className="value" style={{ color: '#d97706' }}>
                                {((selectedReport.passed_cases / (selectedReport.total_cases || 1)) * 100).toFixed(2)}%
                              </div>
                            </SummaryStatCard>
                          </SummaryStatsGrid>

                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '10px 12px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.78rem', fontWeight: 700, color: '#475569', marginBottom: '8px' }}>
                                <Gauge size={14} color="#2563eb" />
                                <span>Latency (Response Time)</span>
                              </div>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '4px' }}>
                                <div>
                                  <div style={{ fontSize: '0.65rem', color: '#64748b', fontWeight: 600 }}>Avg</div>
                                  <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#0f172a' }}>{perfAvgLatency.toLocaleString()} ms</div>
                                </div>
                                <div>
                                  <div style={{ fontSize: '0.65rem', color: '#64748b', fontWeight: 600 }}>Max</div>
                                  <div style={{ fontSize: '0.85rem', fontWeight: 700, color: perfHasFailedLatency ? '#dc2626' : '#0f172a' }}>{perfMaxLatency.toLocaleString()} ms</div>
                                </div>
                                <div>
                                  <div style={{ fontSize: '0.65rem', color: '#64748b', fontWeight: 600 }}>Min</div>
                                  <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#0f172a' }}>{perfMinLatency.toLocaleString()} ms</div>
                                </div>
                              </div>
                            </div>

                            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '10px 12px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.78rem', fontWeight: 700, color: '#475569', marginBottom: '8px' }}>
                                <HardDrive size={14} color="#9333ea" />
                                <span>Memory Usage</span>
                              </div>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '4px' }}>
                                <div>
                                  <div style={{ fontSize: '0.65rem', color: '#64748b', fontWeight: 600 }}>Avg</div>
                                  <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#0f172a' }}>{perfAvgMemory.toFixed(2)} MB</div>
                                </div>
                                <div>
                                  <div style={{ fontSize: '0.65rem', color: '#64748b', fontWeight: 600 }}>Max</div>
                                  <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#0f172a' }}>{perfMaxMemory.toFixed(2)} MB</div>
                                </div>
                                <div>
                                  <div style={{ fontSize: '0.65rem', color: '#64748b', fontWeight: 600 }}>P95</div>
                                  <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#0f172a' }}>{perfP95Memory.toFixed(2)} MB</div>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div style={{ display: 'flex', gap: '16px', fontSize: '0.72rem', color: '#64748b', marginTop: '6px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#22c55e' }}></span>
                              Passed (within threshold)
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#ef4444' }}></span>
                              Failed (exceeded threshold)
                            </div>
                          </div>
                        </SummaryPanel>
                      );
                    })()
                  ) : (
                    <SummaryPanel>
                      <h3 style={{ fontSize: '0.85rem', fontWeight: 700, color: '#334155', margin: 0 }}>Summary</h3>
                      
                      <DialSection>
                        <RadialGauge 
                          score={selectedReport.avg_test_score || 0} 
                          threshold={selectedReport.pass_threshold || 0.7} 
                        />
                      </DialSection>
                      
                      <div style={{ textAlign: 'center', fontSize: '0.8rem', color: '#64748b', fontWeight: 600, marginTop: '-8px' }}>
                        Pass Threshold: {(selectedReport.pass_threshold || 0.7).toFixed(2)}
                      </div>

                      <SummaryStatsGrid>
                        <SummaryStatCard className="total">
                          <div className="label">Total Cases</div>
                          <div className="value">{selectedReport.total_cases}</div>
                        </SummaryStatCard>
                        <SummaryStatCard className="passed">
                          <div className="label">Passed Cases</div>
                          <div className="value">{selectedReport.passed_cases}</div>
                        </SummaryStatCard>
                        <SummaryStatCard className="failed">
                          <div className="label">Failed Cases</div>
                          <div className="value">{selectedReport.failed_cases}</div>
                        </SummaryStatCard>
                        <SummaryStatCard className="rate">
                          <div className="label">Pass Rate</div>
                          <div className="value">
                            {((selectedReport.passed_cases / (selectedReport.total_cases || 1)) * 100).toFixed(2)}%
                          </div>
                        </SummaryStatCard>
                      </SummaryStatsGrid>
                    </SummaryPanel>
                  )}
                </DetailGrid>

                {/* 3. Test Cases List Section */}
                {selectedReport.status === 'running' ? (
                  <Card style={{ padding: '30px', textAlign: 'center', color: '#64748b' }}>
                    <div className="spinner" style={{
                      width: '32px',
                      height: '32px',
                      border: '3px solid #f1f5f9',
                      borderTopColor: 'var(--primary-500)',
                      borderRadius: '50%',
                      margin: '0 auto 16px',
                      animation: 'spin 0.8s linear infinite'
                    }}></div>
                    <div style={{ fontWeight: 600, color: '#334155' }}>Job execution in progress</div>
                    <p style={{ fontSize: '0.8rem', marginTop: '4px' }}>This run was triggered asynchronously. Once the agent package wraps up, this details view will refresh automatically.</p>
                  </Card>
                ) : (selectedReport.eval_type === 'accuracy' || selectedReport.eval_type === 'agent_as_judge') ? (
                  <div>
                    <SectionHeader>
                      <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#1e293b', margin: 0 }}>Test Cases ({filteredCases.length})</h3>
                      <FilterControls>
                        <SelectInput 
                          value={statusFilter} 
                          onChange={(e) => setStatusFilter(e.target.value)}
                        >
                          <option value="all">All Status</option>
                          <option value="passed">Passed</option>
                          <option value="failed">Failed</option>
                        </SelectInput>
                        <Button onClick={toggleExpandAll} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.8rem', padding: '6px 12px' }}>
                          <ExternalLink size={12} /> {isAllExpanded ? 'Collapse All' : 'Expand All'}
                        </Button>
                      </FilterControls>
                    </SectionHeader>

                    <TestCasesList>
                      {filteredCases.map((c, i) => {
                        const expanded = !!expandedCases[i];
                        const runs = getRunsList(c);
                        const numIterations = c.iterations || 1;

                        return (
                          <PremiumCaseCard key={i} passed={c.passed}>
                            <CaseHeaderRow onClick={() => toggleCase(i)}>
                              {c.passed ? (
                                <CheckCircle2 size={18} color="#22c55e" style={{ flexShrink: 0 }} />
                              ) : (
                                <XCircle size={18} color="#ef4444" style={{ flexShrink: 0 }} />
                              )}
                              <div className="name-block">
                                {c.name}
                                <span style={{
                                  background: c.passed ? '#dcfce7' : '#fee2e2',
                                  color: c.passed ? '#15803d' : '#b91c1c',
                                  padding: '2px 8px',
                                  borderRadius: '20px',
                                  fontSize: '0.68rem',
                                  fontWeight: 700
                                }}>
                                  {c.passed ? 'Passed' : 'Failed'}
                                </span>
                              </div>

                              <div className="meta-details">
                                <span>Query: <b>{c.query}</b></span>
                                <span className="sep">|</span>
                                <span>Score: <b>{c.score.toFixed(2)}</b></span>
                                <span className="sep">|</span>
                                <span>Avg Score: <b>{(c.score * 10).toFixed(2)} / 10</b></span>
                                <span className="sep">|</span>
                                <span>Eval Type: <b>{c.eval_type}</b></span>
                                <span className="sep">|</span>
                                <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                  Iterations: <b>{numIterations}</b>
                                  <Info size={12} color="#94a3b8" title="Number of runs evaluated" />
                                </span>
                              </div>

                              {expanded ? <ChevronUp size={16} color="#94a3b8" /> : <ChevronDown size={16} color="#94a3b8" />}
                            </CaseHeaderRow>

                            {expanded && (
                              <CaseContent>
                                {renderReason(c, i)}

                                <div style={{ marginTop: '4px' }}>
                                  <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#475569', marginBottom: '8px' }}>
                                    Evaluation Runs ({runs.length})
                                  </div>
                                  <ExecutionRunsTable>
                                    <thead>
                                      {selectedReport.eval_type === 'agent_as_judge' ? (
                                        <tr>
                                          <th style={{ width: '180px' }}>Run ID</th>
                                          <th style={{ width: '90px' }}>Score</th>
                                          <th style={{ width: '90px' }}>Pass Rate</th>
                                          <th>Output (Truncated)</th>
                                          <th>Reason (Truncated)</th>
                                          <th>Criteria</th>
                                          <th style={{ width: '90px' }}>Passed</th>
                                          <th style={{ width: '40px' }}></th>
                                        </tr>
                                      ) : (
                                        <tr>
                                          <th style={{ width: '60px' }}>Run</th>
                                          <th style={{ width: '90px' }}>Score</th>
                                          <th>Output (Truncated)</th>
                                          <th>Reason (Truncated)</th>
                                          <th>Expected Output (Truncated)</th>
                                          <th style={{ width: '40px' }}></th>
                                        </tr>
                                      )}
                                    </thead>
                                    <tbody>
                                      {runs.map((r, rIdx) => (
                                        <tr key={rIdx}>
                                          {selectedReport.eval_type === 'agent_as_judge' ? (
                                            <>
                                              <td style={{ fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all' }}>
                                                {r.runId}
                                              </td>
                                              <td>
                                                <ScoreBadge color={r.score >= 7 ? 'green' : 'red'}>
                                                  {r.score} / 10
                                                </ScoreBadge>
                                              </td>
                                              <td style={{ fontWeight: 600 }}>
                                                {r.passRate}%
                                              </td>
                                              <td style={{ color: '#475569' }}>
                                                {r.output ? (r.output.length > 50 ? `${r.output.slice(0, 50)}...` : r.output) : '—'}
                                              </td>
                                              <td style={{ color: '#475569' }}>
                                                {r.reason ? (r.reason.length > 50 ? `${r.reason.slice(0, 50)}...` : r.reason) : '—'}
                                              </td>
                                              <td style={{ color: '#475569', fontSize: '0.78rem' }}>
                                                {r.criteria ? (r.criteria.length > 60 ? `${r.criteria.slice(0, 60)}...` : r.criteria) : '—'}
                                              </td>
                                              <td>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: r.passed ? '#22c55e' : '#ef4444', fontWeight: 600, fontSize: '0.8rem' }}>
                                                  {r.passed ? <CheckCircle2 size={14} color="#22c55e" /> : <XCircle size={14} color="#ef4444" />}
                                                  {r.passed ? 'Yes' : 'No'}
                                                </div>
                                              </td>
                                            </>
                                          ) : (
                                            <>
                                              <td style={{ fontWeight: 600 }}>{r.runNum}</td>
                                              <td>
                                                <ScoreBadge color={r.score >= 7 ? 'green' : 'red'}>
                                                  {r.score} / 10
                                                </ScoreBadge>
                                              </td>
                                              <td style={{ color: '#475569' }}>
                                                {r.output ? (r.output.length > 50 ? `${r.output.slice(0, 50)}...` : r.output) : '—'}
                                              </td>
                                              <td style={{ color: '#475569' }}>
                                                {r.reason ? (r.reason.length > 50 ? `${r.reason.slice(0, 50)}...` : r.reason) : '—'}
                                              </td>
                                              <td style={{ color: '#475569' }}>
                                                {r.expected_output ? (r.expected_output.length > 50 ? `${r.expected_output.slice(0, 50)}...` : r.expected_output) : '—'}
                                              </td>
                                            </>
                                          )}
                                          <td>
                                            <Button 
                                              style={{ padding: '4px', borderColor: 'transparent', background: 'none' }}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setActiveModalRun({ tcName: c.name, ...r });
                                              }}
                                            >
                                              <ExternalLink size={13} color="#2563eb" />
                                            </Button>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </ExecutionRunsTable>
                                </div>
                              </CaseContent>
                            )}
                          </PremiumCaseCard>
                        );
                      })}
                    </TestCasesList>
                  </div>
                ) : selectedReport.eval_type === 'reliability' ? (
                  <>
                    <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#1e293b', marginBottom: '12px' }}>Test Case Results</h3>
                    {selectedReport.result?.case_results?.map((c, i) => {
                      const expanded = expandedCases[i];
                      
                      return (
                        <CaseResultRow key={i}>
                          <CaseHeader onClick={() => toggleCase(i)}>
                            <span style={{
                              background: c.passed ? '#dcfce7' : '#fee2e2',
                              color: c.passed ? '#15803d' : '#b91c1c',
                              padding: '3px 8px',
                              borderRadius: '20px',
                              fontSize: '0.75rem',
                              fontWeight: 700
                            }}>
                              {c.passed ? 'Passed' : 'Failed'}
                            </span>
                            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#334155', flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {c.input}
                            </span>
                            {expanded ? <ChevronUp size={16} color="#94a3b8" /> : <ChevronDown size={16} color="#94a3b8" />}
                          </CaseHeader>
                          
                          {expanded && (
                            <CaseBody>
                              <KVBlock>
                                <div className="kv-label">Prompt Input</div>
                                <div className="kv-val">{c.input}</div>
                              </KVBlock>
                              {c.expected_tool_calls && (
                                <KVBlock>
                                  <div className="kv-label">Tool Calls Validation Sequence</div>
                                  <div className="kv-val" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    {c.expected_tool_calls.map((t, tIdx) => {
                                      const missing = c.missing_tool_calls?.includes(t.tool_name);
                                      return (
                                        <div key={tIdx} style={{ display: 'flex', alignItems: 'center', gap: '6px', color: missing ? '#ef4444' : '#22c55e', fontSize: '0.8rem', fontWeight: 600 }}>
                                          {missing ? <XCircle size={12} /> : <CheckCircle size={12} />}
                                          <span>{t.tool_name} {missing && '(Missing / dropped)'}</span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </KVBlock>
                              )}
                            </CaseBody>
                          )}
                        </CaseResultRow>
                      );
                    })}
                  </>
                ) : selectedReport.eval_type === 'performance' ? (
                  <div>
                    <SectionHeader>
                      <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#1e293b', margin: 0 }}>Test Cases ({filteredCases.length})</h3>
                      <FilterControls>
                        <SelectInput 
                          value={statusFilter} 
                          onChange={(e) => setStatusFilter(e.target.value)}
                        >
                          <option value="all">All Status</option>
                          <option value="passed">Passed</option>
                          <option value="failed">Failed</option>
                        </SelectInput>
                        <Button onClick={toggleExpandAll} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.8rem', padding: '6px 12px' }}>
                          <ExternalLink size={12} /> {isAllExpanded ? 'Collapse All' : 'Expand All'}
                        </Button>
                      </FilterControls>
                    </SectionHeader>

                    <TestCasesList>
                      {filteredCases.map((c, i) => {
                        const expanded = expandedCases[i] !== false; // expanded by default

                        return (
                          <PremiumCaseCard key={i} passed={c.passed} style={{ borderLeft: c.passed ? '4px solid #22c55e' : '4px solid #ef4444', marginBottom: '14px', borderRadius: '10px', border: '1px solid #e2e8f0', borderLeftWidth: '4px' }}>
                            <CaseHeaderRow onClick={() => toggleCase(i)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
                                {c.passed ? (
                                  <CheckCircle2 size={20} color="#22c55e" style={{ flexShrink: 0 }} />
                                ) : (
                                  <XCircle size={20} color="#ef4444" style={{ flexShrink: 0 }} />
                                )}
                                <div style={{ flex: 1 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <span style={{ fontWeight: 700, fontSize: '0.92rem', color: '#0f172a' }}>{c.name}</span>
                                    <span style={{
                                      background: c.passed ? '#dcfce7' : '#fee2e2',
                                      color: c.passed ? '#15803d' : '#b91c1c',
                                      padding: '2px 8px',
                                      borderRadius: '20px',
                                      fontSize: '0.68rem',
                                      fontWeight: 700
                                    }}>
                                      {c.passed ? 'Passed' : 'Failed'}
                                    </span>
                                  </div>
                                  <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: '4px' }}>
                                    <span>Query: <b>{c.query}</b></span>
                                    <span style={{ margin: '0 8px', color: '#cbd5e1' }}>|</span>
                                    <span>Iterations: <b>{c.iterations}</b></span>
                                  </div>
                                  {c.reason && (
                                    <div style={{ fontSize: '0.75rem', color: c.passed ? '#15803d' : '#b91c1c', marginTop: '4px', fontWeight: 500 }}>
                                      Reason: {c.reason}
                                    </div>
                                  )}
                                </div>
                              </div>
                              {expanded ? <ChevronUp size={16} color="#94a3b8" /> : <ChevronDown size={16} color="#94a3b8" />}
                            </CaseHeaderRow>

                            {expanded && (
                              <div style={{ padding: '0 16px 16px 16px', display: 'grid', gridTemplateColumns: '1fr 1fr 1.6fr 1.6fr', gap: '14px', alignItems: 'start' }}>
                                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '10px 12px' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                    <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#334155' }}>Latency (ms)</span>
                                    <span style={{
                                      background: c.passed_latency !== false ? '#dcfce7' : '#fee2e2',
                                      color: c.passed_latency !== false ? '#15803d' : '#b91c1c',
                                      padding: '1px 6px',
                                      borderRadius: '12px',
                                      fontSize: '0.65rem',
                                      fontWeight: 700
                                    }}>
                                      {c.passed_latency !== false ? 'Passed' : 'Failed'}
                                    </span>
                                  </div>
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '0.75rem' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                      <span style={{ color: '#64748b' }}>Avg</span>
                                      <span style={{ fontWeight: 700, color: '#0f172a' }}>{Math.round(c.avg_latency_ms || 0).toLocaleString()}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                      <span style={{ color: '#64748b' }}>Max</span>
                                      <span style={{ fontWeight: 700, color: c.passed_latency === false ? '#dc2626' : '#0f172a' }}>{Math.round(c.max_latency_ms || 0).toLocaleString()}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                      <span style={{ color: '#64748b' }}>Min</span>
                                      <span style={{ fontWeight: 700, color: c.passed_latency === false ? '#dc2626' : '#0f172a' }}>{Math.round(c.min_latency_ms || 0).toLocaleString()}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                      <span style={{ color: '#64748b' }}>P95</span>
                                      <span style={{ fontWeight: 700, color: c.passed_latency === false ? '#dc2626' : '#0f172a' }}>
                                        {c.evaluation_result?.p95_run_time ? Math.round(c.evaluation_result.p95_run_time * 1000).toLocaleString() : Math.round(c.max_latency_ms || 0).toLocaleString()}
                                      </span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #e2e8f0', paddingTop: '4px', marginTop: '2px' }}>
                                      <span style={{ color: '#64748b', fontWeight: 600 }}>Threshold</span>
                                      <span style={{ fontWeight: 700, color: '#475569' }}>
                                        {c.latency_threshold_ms ? c.latency_threshold_ms.toLocaleString() : '—'}
                                      </span>
                                    </div>
                                  </div>
                                </div>

                                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '10px 12px' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                    <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#334155' }}>Memory (MB)</span>
                                    <span style={{
                                      background: c.passed_memory !== false ? '#dcfce7' : '#fee2e2',
                                      color: c.passed_memory !== false ? '#15803d' : '#b91c1c',
                                      padding: '1px 6px',
                                      borderRadius: '12px',
                                      fontSize: '0.65rem',
                                      fontWeight: 700
                                    }}>
                                      {c.passed_memory !== false ? 'Passed' : 'Failed'}
                                    </span>
                                  </div>
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '0.75rem' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                      <span style={{ color: '#64748b' }}>Avg</span>
                                      <span style={{ fontWeight: 700, color: '#0f172a' }}>{(c.avg_memory_mb || 0).toFixed(2)}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                      <span style={{ color: '#64748b' }}>Max</span>
                                      <span style={{ fontWeight: 700, color: '#0f172a' }}>{(c.max_memory_mb || 0).toFixed(2)}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                      <span style={{ color: '#64748b' }}>Min</span>
                                      <span style={{ fontWeight: 700, color: '#0f172a' }}>
                                        {(c.evaluation_result?.min_memory_usage || c.avg_memory_mb || 0).toFixed(2)}
                                      </span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                      <span style={{ color: '#64748b' }}>P95</span>
                                      <span style={{ fontWeight: 700, color: '#0f172a' }}>
                                        {(c.evaluation_result?.p95_memory_usage || c.max_memory_mb || 0).toFixed(2)}
                                      </span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #e2e8f0', paddingTop: '4px', marginTop: '2px' }}>
                                      <span style={{ color: '#64748b', fontWeight: 600 }}>Threshold</span>
                                      <span style={{ fontWeight: 700, color: '#475569' }}>
                                        {c.memory_threshold_mb ? c.memory_threshold_mb.toFixed(2) : '—'}
                                      </span>
                                    </div>
                                  </div>
                                </div>

                                <IterationLineChart 
                                  title="Latency Over Iterations" 
                                  unit="ms" 
                                  data={(c.evaluation_result?.run_times || [c.avg_latency_ms / 1000]).map(val => val < 1000 ? Math.round(val * 1000) : Math.round(val))} 
                                  isPassed={c.passed_latency !== false} 
                                />

                                <IterationLineChart 
                                  title="Memory Over Iterations" 
                                  unit="MB" 
                                  data={c.evaluation_result?.memory_usages || [c.avg_memory_mb]} 
                                  color="#9333ea" 
                                  isPassed={c.passed_memory !== false} 
                                />
                              </div>
                            )}
                          </PremiumCaseCard>
                        );
                      })}
                    </TestCasesList>

                    <div style={{
                      margin: '20px 0 10px 0',
                      padding: '10px 16px',
                      background: '#f0f9ff',
                      border: '1px solid #bae6fd',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '0.78rem',
                      color: '#0369a1'
                    }}>
                      <Info size={16} color="#0284c7" />
                      <span>Latency is measured in milliseconds (ms). Memory usage is measured in megabytes (MB). Lower values are better for both metrics.</span>
                    </div>
                  </div>
                ) : (
                  <Card style={{ padding: '24px', textAlign: 'center', color: '#94a3b8' }}>
                    No results details available for this report.
                  </Card>
                )}

                {/* Iteration Run Detail Modal overlay */}
                {activeModalRun && (
                  <RunDetailModalOverlay onClick={() => setActiveModalRun(null)}>
                    <RunDetailModalContainer onClick={(e) => e.stopPropagation()}>
                      <RunDetailModalHeader>
                        <h3>Iteration Run Details — {activeModalRun.tcName} (Run {activeModalRun.runNum})</h3>
                        <Button style={{ padding: '4px', borderColor: 'transparent', color: '#64748b' }} onClick={() => setActiveModalRun(null)}>
                          <X size={16} />
                        </Button>
                      </RunDetailModalHeader>
                      <RunDetailModalBody>
                        <KVBlock>
                          <div className="kv-label">Run Score</div>
                          <div style={{ marginTop: '4px' }}>
                            <ScoreBadge color={activeModalRun.score >= 7 ? 'green' : 'red'}>
                              {activeModalRun.score} / 10
                            </ScoreBadge>
                          </div>
                        </KVBlock>
                        {activeModalRun.criteria ? (
                          <KVBlock>
                            <div className="kv-label">Scoring Criteria Rubric</div>
                            <div className="kv-val" style={{ whiteSpace: 'pre-wrap' }}>{activeModalRun.criteria}</div>
                          </KVBlock>
                        ) : (
                          <KVBlock>
                            <div className="kv-label">Expected Output (Ground Truth)</div>
                            <div className="kv-val">{activeModalRun.expected_output || '—'}</div>
                          </KVBlock>
                        )}
                        <KVBlock>
                          <div className="kv-label">Actual Agent Output</div>
                          <div className="kv-val" style={{ background: '#faf5ff', borderColor: '#f3e8ff' }}>{activeModalRun.output || '—'}</div>
                        </KVBlock>
                        <KVBlock>
                          <div className="kv-label">Judge Reasoning</div>
                          <div className="kv-val" style={{ background: '#f0fdf4', borderColor: '#dcfce7', fontFamily: 'inherit', color: '#1e293b' }}>{activeModalRun.reason || '—'}</div>
                        </KVBlock>
                      </RunDetailModalBody>
                    </RunDetailModalContainer>
                  </RunDetailModalOverlay>
                )}
              </DetailLayout>
            );
          })()
        )}
      </motion.div>

      {/* Toast Render */}
      {toastMsg && (
        <Toast>
          <AlertCircle size={16} color="var(--primary-300)" />
          <span>{toastMsg}</span>
        </Toast>
      )}
    </PageContainer>
  );
};

export default EvalReports;
